import { useCallback, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useUploadImage } from '@/application/services/upload.service'
import type { TiptapEditor } from '@/domains/types/editor'

const ACCEPTED_MIME_REGEX = /^image\/(jpeg|jpg|png|webp|gif)$/
const MAX_IMAGE_BYTES = 30 * 1024 * 1024 // 30MB — matches the backend upload cap

export type EditorImageUploadErrorReason = 'too_large' | 'invalid_type' | 'upload_failed'

interface UseEditorImageUploadOptions {
  /** Called whenever an image is rejected client-side or fails to upload. */
  onUploadError?: (reason: EditorImageUploadErrorReason) => void
}

interface EditorFileInputProps {
  ref: React.RefObject<HTMLInputElement | null>
  type: 'file'
  accept: string
  className: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

interface UseEditorImageUploadResult {
  isUploading: boolean
  /** Opens the hidden `<input type="file">` rendered via `fileInputProps`. */
  openFilePicker: (editor: TiptapEditor | null) => void
  fileInputProps: EditorFileInputProps
  /** Wire into `editorProps.handlePaste`. Returns `true` when an image was handled. */
  handlePaste: (event: ClipboardEvent, editor: TiptapEditor) => boolean
  /** Wire into `editorProps.handleDrop`. Returns `true` when an image was handled. */
  handleDrop: (event: DragEvent, editor: TiptapEditor) => boolean
}

/**
 * Wraps `useUploadImage()` with the client-side validation, file-picker
 * plumbing and paste/drop handlers shared by every `RichTextEditor`
 * instance. On a successful upload the image is inserted at the current
 * selection as a TipTap `image` node.
 */
export function useEditorImageUpload(
  options: UseEditorImageUploadOptions = {},
): UseEditorImageUploadResult {
  const { onUploadError } = options
  const { mutateAsync: uploadImage, isPending } = useUploadImage()
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingEditorRef = useRef<TiptapEditor | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const insertImage = useCallback((editor: TiptapEditor, imageUrl: string, imageAlt: string) => {
    editor.chain().focus().setImage({ src: imageUrl, alt: imageAlt }).run()
  }, [])

  const uploadFile = useCallback(
    async (file: File, editor: TiptapEditor) => {
      if (!ACCEPTED_MIME_REGEX.test(file.type)) {
        onUploadError?.('invalid_type')
        return
      }
      if (file.size > MAX_IMAGE_BYTES) {
        onUploadError?.('too_large')
        return
      }
      setIsUploading(true)
      try {
        const imageUrl = await uploadImage(file)
        insertImage(editor, imageUrl, file.name)
      } catch {
        onUploadError?.('upload_failed')
      } finally {
        setIsUploading(false)
      }
    },
    [insertImage, onUploadError, uploadImage],
  )

  const openFilePicker = useCallback((editor: TiptapEditor | null) => {
    pendingEditorRef.current = editor
    inputRef.current?.click()
  }, [])

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      const editor = pendingEditorRef.current
      event.target.value = '' // reset so the same file can be re-selected
      if (file && editor) void uploadFile(file, editor)
    },
    [uploadFile],
  )

  const handlePaste = useCallback(
    (event: ClipboardEvent, editor: TiptapEditor): boolean => {
      const file = Array.from(event.clipboardData?.items ?? [])
        .find((item) => item.kind === 'file' && item.type.startsWith('image/'))
        ?.getAsFile()
      if (!file) return false
      event.preventDefault()
      void uploadFile(file, editor)
      return true
    },
    [uploadFile],
  )

  const handleDrop = useCallback(
    (event: DragEvent, editor: TiptapEditor): boolean => {
      const file = Array.from(event.dataTransfer?.files ?? []).find((candidate) =>
        candidate.type.startsWith('image/'),
      )
      if (!file) return false
      event.preventDefault()
      void uploadFile(file, editor)
      return true
    },
    [uploadFile],
  )

  return {
    isUploading: isUploading || isPending,
    openFilePicker,
    fileInputProps: {
      ref: inputRef,
      type: 'file',
      accept: 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
      className: 'hidden',
      onChange: handleFileInputChange,
    },
    handlePaste,
    handleDrop,
  }
}
